import { useQuery } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  getIncentivesByPlatformIdReferralAchievementsByPersonId,
  getIncentivesByPlatformIdReferralHistoryByPersonId,
  getIncentivesByPlatformLocationIdActiveSignupReward,
  getPlatformTheme,
  getUser,
} from "api/methods";
import { useTranslation } from "@/core/i18n";
import {
  getAwardedReferralRewards,
  ReferralRewardsAchievements,
  ReferralRewardsAcknowledgeRewardsModal,
  ReferralRewardsReferralHistory,
} from "@/modules/referralRewards";
import TopNav from "@/molecules/navbars/TopNav";
import { Models } from "api/types";

type ReferralRewardsContainerProps = {
  platform: Models<"PlatformModel">;
  referralReward?: Models<"DisplayAvailableReferralReward">;
  referralHistoryForPerson?: Models<"DisplayReferralHistoryForPerson">;
  achievementProgress?: Models<"DisplayAchievementProgress">;
};

export const ReferralRewardsContainer = ({
  platform,
  referralReward,
  referralHistoryForPerson: initialReferralHistoryForPerson,
  achievementProgress: initialAchievementProgress,
}: ReferralRewardsContainerProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: user } = useQuery({ ...getUser.keyed() });

  const { data: referralHistoryForPerson } = useQuery({
    ...getIncentivesByPlatformIdReferralHistoryByPersonId.keyed({
      platformId: platform.id || "",
      personId: user?.id || "",
    }),
    enabled: !!platform.id && !!user?.id,
    initialData: initialReferralHistoryForPerson,
    onError: () => null,
  });

  const { data: achievementProgress } = useQuery({
    ...getIncentivesByPlatformIdReferralAchievementsByPersonId.keyed({
      platformId: platform.id || "",
      personId: user?.id || "",
    }),
    enabled: !!platform.id && !!user?.id,
    initialData: initialAchievementProgress,
    onError: () => null,
  });

  const { data: awardedReferralRewardsResponse } = useQuery({
    queryKey: getAwardedReferralRewards.key({
      platformId: platform.id || "",
      personId: user?.id || "",
    }),
    queryFn: () =>
      getAwardedReferralRewards({
        platformId: platform.id || "",
        personId: user?.id || "",
      }),
    enabled: !!platform.id && !!user?.id,
    onError: () => null,
  });

  const { data: activeSignupReward } = useQuery({
    ...getIncentivesByPlatformLocationIdActiveSignupReward.keyed({
      platformLocationId: user?.platformLocation?.id ?? "",
    }),
    enabled: !!user?.platformLocation?.id,
    onError: () => null,
  });

  const { data: theme } = useQuery({ ...getPlatformTheme.keyed() });

  const hasBonusRewardActive =
    referralReward && activeSignupReward ? referralReward?.bonusRewardTitle?.text : undefined;

  const isCashOrRepeatCash = referralReward?.referralRewardType === "RepeatCash";

  const shouldShowAchievements = isCashOrRepeatCash;

  return (
    <div className="ReferralRewardsContainer flex h-full flex-col bg-gray-50">
      <TopNav startIconSVG={ChevronLeft} onStartIconClick={() => navigate(-1)}>
        <div className="flex max-h-[32px] items-center">
          <h1 className="whitespace-nowrap text-xl">{t("referralRewardsContainer.heading")}</h1>
        </div>
      </TopNav>

      {shouldShowAchievements && (
        <ReferralRewardsAchievements
          brandColor={theme?.brandColor ?? "#000000"}
          achievementProgress={achievementProgress ?? undefined}
          redeemableAwardedReferralRewards={
            awardedReferralRewardsResponse?.redeemableAwardedReferralRewards || []
          }
          nextBonusRewardTitle={hasBonusRewardActive}
          referralReward={referralReward}
        />
      )}

      <ReferralRewardsReferralHistory history={referralHistoryForPerson?.referralHistory ?? []} />

      <ReferralRewardsAcknowledgeRewardsModal
        platformId={platform.id}
        onCloseAfterInteracting={() => null}
      />
    </div>
  );
};
